import React, { useRef, useState, useEffect } from "react";
import { IOktaMFAFactor } from "../../../../Models/OktaModels/IOktaMFAFactor";
import { Modal, Row, Col, Button, Carousel, Input, Spin, Alert } from "antd";

import "./MultiFactorModal.css";

interface IMultiFactorModalProps {
    allMFAFactors: IOktaMFAFactor[];
    selectedMFAFactorID: string;
    waitingForMFAResponse: boolean;
    mfaResult: string;
    successCallback: () => void;
    setAllMFAFactors: (factors: IOktaMFAFactor[]) => void;
    setSelectedFactorID: (id: string) => void;
    setWaitingForMFAResponse: (waiting: boolean) => void;
    IssueMFAChallenge: (factor: IOktaMFAFactor) => void;
    IssuePushMFAChallenge: (factor: IOktaMFAFactor) => void;
    SubmitMFAChallengeResponse: (
        factor: IOktaMFAFactor,
        response: string
    ) => void;
    PollForPushResponse: (factor: IOktaMFAFactor) => void;
}

const MultiFactorModal: React.FC<IMultiFactorModalProps> = (
    props: IMultiFactorModalProps
) => {
    //Create carousel ref to programatically change pages
    const carousel = useRef<Carousel>();

    //useState for response code
    const [responseCode, setResponseCode] = useState<string>("");

    //Handler for cancel button
    const onCancel = () => {
        props.setAllMFAFactors(null);
        setResponseCode("");
    };

    //Function to be called when props.mfaResult === "SUCCESS"
    const executeSuccessCallback = () => {
        if (props.mfaResult === "SUCCESS") {
            setTimeout(() => {
                props.successCallback();
                props.setAllMFAFactors(null);
            }, 2000);
        }
    };
    useEffect(executeSuccessCallback, [props.mfaResult]);

    //User selects MFA method to use
    const onMFAClick = (event: any) => {
        props.setWaitingForMFAResponse(false);

        const factorId = event.target.id;
        const factor = selectedFactor(factorId);

        if (factor.factorType === "push") {
            props.IssuePushMFAChallenge(factor);
        } else {
            props.IssueMFAChallenge(factor);
        }

        props.setSelectedFactorID(factorId);

        let gotoSlide: number;

        switch (factor.factorType) {
            case "sms":
                gotoSlide = 1;
                break;
            case "token:software:totp":
                gotoSlide = 2;
                break;
            case "push":
                gotoSlide = 3;
                break;
            case "email":
                gotoSlide = 4;
                break;
            default:
                gotoSlide = 0;
        }

        carousel.current.goTo(gotoSlide);
    };

    const onEnterPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.keyCode === 13) {
            document.getElementById("mfaSubmit").click();
        }
    };

    const onResponseCodeChange = (event: any) => {
        setResponseCode(event.target.value);
    };

    const onCodeSubmit = () => {
        const factor = selectedFactor(props.selectedMFAFactorID);
        props.SubmitMFAChallengeResponse(factor, responseCode);
    };

    const selectedFactor = (selectedMFAFactorID: string): IOktaMFAFactor => {
        const factor = props.allMFAFactors.find((factor) => {
            return factor.id === selectedMFAFactorID;
        });

        return factor;
    };

    const factorButtons = props.allMFAFactors.map((factor) => {
        let friendlyName: string;

        switch (factor.factorType) {
            case "email":
                friendlyName = "Send code via email";
                break;
            case "sms":
                friendlyName = "Send code via text";
                break;
            case "token:software:totp":
                friendlyName = "Okta Verify app - code";
                break;
            case "push":
                friendlyName = "Okta Verify app - push notification";
                break;
            default:
                friendlyName = "";
        }

        return (
            <Row key={factor.id} type="flex" justify="center">
                <Col span={18}>
                    <Button
                        id={factor.id}
                        onClick={onMFAClick}
                        type="primary"
                        size="large"
                        className="user-profile-mfa-factor-button user-profile-full-width"
                    >
                        {friendlyName}
                    </Button>
                </Col>
            </Row>
        );
    });

    const factorSelectionPage = (
        <div key="0">
            <h3>
                We need to confirm your identity to make the requested changes. Please
                select an authentication method (Click the 'X' in the top-right corner
                to cancel).
            </h3>
            {factorButtons}
        </div>
    );

    const mfaSubmitButton = (
        <Button
            id="mfaSubmit"
            type="primary"
            className="user-profile-mfa-factor-button user-profile-full-width"
            onClick={onCodeSubmit}
            disabled={responseCode === ""}
        >
            OK
        </Button>
    );

    let mfaStatus: JSX.Element;
    let mfaStatusSpan: number;

    if (props.waitingForMFAResponse) {
        mfaStatus = <Spin tip="Awaiting verification..." />;
        mfaStatusSpan = 8;
    } else {
        switch (props.mfaResult) {
            case "":
                mfaStatus = mfaSubmitButton;
                mfaStatusSpan = 4;
                break;
            case "SUCCESS":
                mfaStatus = (
                    <Alert message="Success! Performing update..." type="success" />
                );
                mfaStatusSpan = 18;
                break;
            case "WAITING":
                mfaStatus = <Spin tip="Awaiting verification..." />;
                mfaStatusSpan = 8;
                break;
            case "REJECTED":
                mfaStatus = (
                    <Alert message="Authentication request rejected" type="error" />
                );
                mfaStatusSpan = 18;
                break;
            case "TIMEOUT":
                mfaStatus = (
                    <Alert
                        message="Authentication request timed out. Close this window and try again."
                        type="error"
                    />
                );
                mfaStatusSpan = 18;
                break;
            case "Your token doesn't match our records. Please try again.":
            case "Your code doesn't match our records. Please try again.":
            case "Your passcode doesn't match our records. Please try again.":
                mfaStatus = (
                    <>
                        <Alert message={props.mfaResult} type="error" />
                        {mfaSubmitButton}
                    </>
                );
                mfaStatusSpan = 18;
                break;
            default:
                mfaStatus = <Alert message={props.mfaResult} type="error" />;
                mfaStatusSpan = 18;
                break;
        }
    }

    const smsResponsePage = (
        <div key="1">
            <h3>
                Please enter the code sent to your device in the text box below, then
                click "OK":
            </h3>
            <Row type="flex" justify="center">
                <Col span={18}>
                    <Input
                        className="user-profile-full-width"
                        value={responseCode}
                        onChange={onResponseCodeChange}
                        disabled={props.waitingForMFAResponse}
                        onKeyUp={onEnterPress}
                    />
                </Col>
            </Row>
            <Row type="flex" justify="center" className="user-profile-status">
                <Col span={mfaStatusSpan} className="user-profile-row">
                    {mfaStatus}
                </Col>
            </Row>
        </div>
    );

    const oktaVerifyCodePage = (
        <div key="2">
            <h3>
                Please enter the code found in your Okta Verify app in the text box
                below, then click "OK":
            </h3>
            <Row type="flex" justify="center">
                <Col span={18}>
                    <Input
                        className="user-profile-full-width"
                        value={responseCode}
                        onChange={onResponseCodeChange}
                        disabled={props.waitingForMFAResponse}
                        onKeyUp={onEnterPress}
                    />
                </Col>
            </Row>
            <Row type="flex" justify="center" className="user-profile-status">
                <Col span={mfaStatusSpan} className="user-profile-row">
                    {mfaStatus}
                </Col>
            </Row>
        </div>
    );

    const oktaVerifyPushPage = (
        <div key="3">
            <h3>
                An authorization request has been sent to your device. Please approve to
                complete this update (This popup will close automatically upon
                approval).
            </h3>
            <Row type="flex" justify="center" className="user-profile-status">
                <Col span={mfaStatusSpan} className="user-profile-row">
                    {mfaStatus}
                </Col>
            </Row>
        </div>
    );

    const emailResponsePage = (
        <div key="4">
            <h3>
                Please enter the code sent to your email in the text box below, then
                click "OK":
            </h3>
            <Row type="flex" justify="center">
                <Col span={18}>
                    <Input
                        className="user-profile-full-width"
                        value={responseCode}
                        onChange={onResponseCodeChange}
                        disabled={props.waitingForMFAResponse}
                        onKeyUp={onEnterPress}
                    />
                </Col>
            </Row>
            <Row type="flex" justify="center" className="user-profile-status">
                <Col span={mfaStatusSpan} className="user-profile-row">
                    {mfaStatus}
                </Col>
            </Row>
        </div>
    );

    const carouselPages = [
        factorSelectionPage,
        smsResponsePage,
        oktaVerifyCodePage,
        oktaVerifyPushPage,
        emailResponsePage,
    ];

    return (
        <Modal
            title="Confirm your identity"
            visible={true}
            onCancel={onCancel}
            maskClosable={false}
            closable={true}
            footer={null}
            destroyOnClose={true}
        >
            <Carousel ref={carousel} dots={false} effect="fade">
                {carouselPages.map((page) => {
                    return page;
                })}
            </Carousel>
        </Modal>
    );
};

export default MultiFactorModal;